/**
 * Toast Store (Zustand)
 * Global state for toast notifications
 * Lightweight, performant, and works outside React components
 */

import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type { ToastMessage, ToastInput } from './toast.types';

interface ToastStore {
  toasts: ToastMessage[];
  show: (msg: ToastInput) => string;
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  warning: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;
  remove: (id: string) => void;
  removeAll: () => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  show: (msg) => {
    const id = nanoid();
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          id,
          duration: msg.duration ?? 4000,
          ...msg,
        },
      ],
    }));
    return id;
  },

  success: (message, title) => {
    const id = nanoid();
    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, type: 'success', message, title, duration: 4000 },
      ],
    }));
    return id;
  },

  error: (message, title) => {
    const id = nanoid();
    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, type: 'error', message, title, duration: 5000 }, // Errors show longer
      ],
    }));
    return id;
  },

  warning: (message, title) => {
    const id = nanoid();
    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, type: 'warning', message, title, duration: 4500 },
      ],
    }));
    return id;
  },

  info: (message, title) => {
    const id = nanoid();
    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, type: 'info', message, title, duration: 4000 },
      ],
    }));
    return id;
  },

  remove: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  removeAll: () => set({ toasts: [] }),
}));

/**
 * Toast helper for use outside React components
 * Can be called from API error handlers, React Query, etc.
 */
export const toast = {
  show: (msg: ToastInput) => useToastStore.getState().show(msg),
  success: (message: string, title?: string) => useToastStore.getState().success(message, title),
  error: (message: string, title?: string) => useToastStore.getState().error(message, title),
  warning: (message: string, title?: string) => useToastStore.getState().warning(message, title),
  info: (message: string, title?: string) => useToastStore.getState().info(message, title),
  remove: (id: string) => useToastStore.getState().remove(id),
  removeAll: () => useToastStore.getState().removeAll(),
};

export default toast;
